import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/apiClient";
import { Brand } from "@/components/Brand";
import { ROLE_LABELS } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { NotificationBell } from "@/components/NotificationBell";
import { ComplianceNotice } from "@/components/ComplianceNotice";
import VersionBanner from "@/components/VersionBanner";
import VersionTag from "@/components/VersionTag";
import { AdminTopNav, AdminRail, AdminMenuItem, menusFor, menuItems, ADMIN_RAIL, ADMIN_ONLY_PATHS } from "@/components/AdminNav";
import { AdminSearch } from "@/components/AdminSearch";
import { LogOut, Menu, X, Lock } from "lucide-react";

function MobileMenu({ role, badge, onClose }) {
  return (
    <div className="fixed inset-0 z-[300] bg-black/30 lg:hidden" onClick={onClose}>
      <div className="h-full w-80 max-w-[85%] overflow-y-auto bg-white p-5 shadow-2xl animate-in slide-in-from-left" onClick={(e) => e.stopPropagation()} data-testid="admin-mobile-menu">
        <div className="mb-4 flex items-center justify-between"><Brand /><button onClick={onClose} data-testid="admin-mobile-close"><X className="h-5 w-5 text-forest" /></button></div>
        {[{ key: "rapidi", label: "Accesso rapido", color: "#1B2380", items: ADMIN_RAIL }, ...menusFor(role)].map((m) => (
          <div key={m.key} className="mb-4">
            <div className="mb-1 text-[11px] font-bold uppercase tracking-wider" style={{ color: m.color }}>{m.label}</div>
            {(m.items || menuItems(m)).map((i) => <AdminMenuItem key={i.label} item={i} badge={badge} onClick={onClose} prefix={`admin-mobile-${m.key}`} />)}
          </div>
        ))}
      </div>
    </div>
  );
}

function NotForRole() {
  return (
    <div className="mx-auto mt-16 max-w-md rounded-2xl border bg-white p-8 text-center" data-testid="admin-not-for-role">
      <Lock className="mx-auto h-8 w-8 text-forest" />
      <h2 className="mt-3 font-heading text-lg font-semibold text-forest">Funzione riservata all'Amministratore</h2>
      <p className="mt-1 text-sm text-muted-foreground">Il tuo ruolo non prevede l'accesso a questa sezione.</p>
    </div>
  );
}

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [badge, setBadge] = useState(0);
  const [mobile, setMobile] = useState(false);
  const doLogout = async () => { await logout(); navigate("/login"); };
  const blocked = user?.role !== "agency_admin" && ADMIN_ONLY_PATHS.some((p) => pathname.startsWith(p));

  useEffect(() => {
    const fetchCount = () => api.get("/admin/adesioni/count").then(({ data }) => setBadge(data.count || 0)).catch(() => {});
    fetchCount();
    const t = setInterval(fetchCount, 60000);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="min-h-screen bg-bone">
      <header className="sticky top-0 z-30 border-b border-border bg-white">
        <div className="mx-auto flex max-w-7xl items-center gap-4 px-5 py-3">
          <button className="lg:hidden" onClick={() => setMobile(true)} data-testid="admin-mobile-open"><Menu className="h-6 w-6 text-forest" /></button>
          <Brand />
          <span className="hidden rounded-full bg-forest px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-white sm:inline">Area Agenzia</span>
          <div className="hidden w-6 lg:block" />
          <AdminSearch />
          <div className="flex-1" />
          <NotificationBell />
          <div className="hidden text-right sm:block">
            <div className="text-sm font-medium text-forest" data-testid="admin-user-name">{user?.name}</div>
            <div className="text-[11px] text-muted-foreground" data-testid="admin-user-role">{ROLE_LABELS[user?.role]}</div>
          </div>
          <Button variant="outline" size="sm" onClick={doLogout} data-testid="admin-logout-button" className="gap-2"><LogOut className="h-4 w-4" /><span className="hidden sm:inline">Esci</span></Button>
        </div>
        <div className="px-4 pb-3 lg:hidden"><AdminSearch mobile /></div>
        <AdminTopNav role={user?.role} badge={badge} />
      </header>
      <div className="flex">
        <AdminRail />
        <div className="min-w-0 flex-1">
          <VersionBanner />
          <main className="mx-auto max-w-7xl p-4 md:p-8">{blocked ? <NotForRole /> : <Outlet />}</main>
          <ComplianceNotice variant="footer" />
          <VersionTag className="!pb-4" />
        </div>
      </div>
      {mobile && <MobileMenu role={user?.role} badge={badge} onClose={() => setMobile(false)} />}
    </div>
  );
}
